import { useState } from 'react'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { KV } from '../components/Controls'
import { SignSheet } from '../components/SignSheet'
import { useStore } from '../state/store'
import { createIntake, intakeNoLabel } from '../lib/api'
import { LABEL, fmt, feePct, splitPct, consignorName, pieceName } from '../lib/types'
import type { Employee, IntakeResult } from '../lib/types'
import { draftReady, cardFromDraft, sourcedFromLine } from './Intake'
import type { CardData } from '../components/IntakeCard'
import { kindOf, sections } from '../../supabase/functions/_shared/listing-description.ts'

export interface RecordedState { result: IntakeResult; by: Employee; type: 'bought' | 'consigned'; sourced?: boolean; from: string; cost: number; min: number; payout: string; split: number; fee: number; card: CardData }

export function IntakeReview() {
  const nav = useNav()
  const { draft: d, intakeNo, consignors, resetDraft, refresh } = useStore()
  const descLines = sections(d.listing, d.brand, d.item, kindOf(d.category, d.item), d.category).map((x) => x.lines[0])
  const [sheet, setSheet] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const bought = d.type === 'bought'
  const sourced = bought && d.sourced && !d.pendingId
  const cons = consignors.find((c) => c.id === d.consignorId)
  const ready = draftReady(d)
  const no = intakeNoLabel(intakeNo)

  const confirm = async (by: Employee) => {
    setBusy(true); setErr('')
    try {
      const result = await createIntake(d, by.employee_id)
      const state: RecordedState = { result, by, type: d.type, sourced, from: sourced ? sourcedFromLine(d.source) : bought ? d.from : consignorName(cons), cost: Number(d.cost || 0), min: Number(d.min || 0), payout: d.pendingId ? 'trade credit' : bought ? LABEL.payout[d.payout] : 'the buyer\'s method', split: splitPct(d), fee: feePct(d), card: { ...cardFromDraft(d, intakeNo, consignorName(cons), by.employee_id), intakeNo: result.intake_no } }
      resetDraft(); refresh().catch(() => {})
      nav('/intake/recorded', { state, replace: true })
    } catch (e) {
      Sentry.captureException(e)
      setErr((e as Error).message || 'Could not record the intake')
      setSheet(false)
    } finally { setBusy(false) }
  }

  return (
    <Page bar={<TopBar back="Intake" backTo="/intake" title="Review intake" caption={`Intake #${no}`} />}>
      {!d.category && <div className="banner">Pick the piece's category before recording. Go back and set Category.</div>}
      {!d.authenticated && <div className="banner">A piece can't be recorded until Christian has authenticated it. Go back and mark Yes.</div>}
      {err && <div className="banner" style={{ background: '#d70015' }}>{err}. Nothing was recorded; check the wifi and try again.</div>}
      <div className="section-title">Deal</div>
      <div className="card review-rows">
        <KV k="Coming in as" v={d.pendingId ? `Traded, trade #${d.tradeNo ?? ''}` : sourced ? 'Sourced by Shane' : bought ? 'Bought' : 'Consigned'} bold />
        {sourced ? <KV k="Where from" v={d.source.trim() || 'Not given'} /> : bought ? <KV k={d.pendingId ? 'From' : 'Bought from'} v={d.from} /> : <KV k="Consignor" v={consignorName(cons)} />}
        {bought ? <KV k={d.pendingId ? 'Trade credit (cost)' : 'Cost paid'} v={fmt(d.cost)} bold /> : <KV k="Starchive fee" v={`${feePct(d)}% (consignor gets ${splitPct(d)}%)`} />}
        {!bought && <KV k="Consignor minimum (floor)" v={Number(d.min) > 0 ? fmt(d.min) : 'None (no floor)'} bold />}
        {sourced && <KV k="Payout" v="None, sourced by Shane" />}
        {bought && !sourced && !d.pendingId && <KV k="Paid out by" v={LABEL.payout[d.payout]} />}
        {bought && <KV k="Whose piece" v={LABEL.whose[d.whose]} />}
      </div>
      <div className="section-title">Piece</div>
      <div className="card review-rows">
        <KV k="Item" v={pieceName(d.brand, d.item, '')} />
        <KV k="Category" v={d.categoryName ? d.categoryName.split(' > ').join(' › ') : 'Not picked'} bold={!d.category} />
        <KV k="Size" v={d.size.trim() || 'Not given'} />
        <KV k="Authenticated by Christian" v={d.authenticated ? 'Yes' : 'Not yet'} bold />
        <KV k="Condition" v={LABEL.cond[d.cond]} />
        <KV k="Deal note" v={d.note || 'None'} />
        <KV k="Measurements" v={d.measurements.trim() || 'Written on the card by hand'} />
        <KV k="Website description" v={descLines.length ? descLines.join(' · ') : 'Left for Shane'} />
        <KV k="Prints" v={`Intake card #${no}`} />
      </div>
      <div className="total"><span>{sourced ? 'Cost (no payout)' : bought ? 'Payout' : 'Starchive fee'}</span><span>{bought ? fmt(d.cost) : `${feePct(d)}%`}</span></div>
      <button type="button" className="cta" disabled={!ready} onClick={() => setSheet(true)}><span>Sign and record</span><span>#{no}</span></button>
      {sheet && <SignSheet onConfirm={confirm} onClose={() => !busy && setSheet(false)} busy={busy} />}
    </Page>
  )
}
